// 视频上传服务（本地存储版本）
const API_BASE = '/api';

// 辅助函数：获取token
const getToken = () => {
  return localStorage.getItem('token');
};

// 辅助函数：API请求
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw new Error('未授权，请重新登录');
    }
    const error = await response.json().catch(() => ({ message: '请求失败' }));
    throw new Error(error.message || '请求失败');
  }

  return response.json();
};

export interface VideoUploadResult {
  key: string;
  url: string;
  fileName: string;
  fileSize: number;
  duration: number;
  resolution: {
    width: number;
    height: number;
  };
  thumbnail: string;
  status: 'ready';
}

export interface MultipartInitResult {
  key: string;
  uploadId: string;
  tempDir: string;
}

export const videoUploadService = {
  // 直接上传视频（小文件）
  uploadVideo: async (file: File): Promise<VideoUploadResult> => {
    const formData = new FormData();
    formData.append('video', file);

    const response = await apiRequest('/video-upload', {
      method: 'POST',
      body: formData,
      // 不设置Content-Type，让浏览器自动设置（包含boundary）
    });
    return response.data;
  },

  // 初始化分片上传
  initMultipartUpload: async (fileName: string, fileSize: number): Promise<MultipartInitResult> => {
    const response = await apiRequest('/video-upload/multipart/init', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileName, fileSize }),
    });
    return response.data;
  },

  // 上传分片
  uploadChunk: async (
    uploadId: string,
    chunkNumber: number,
    totalChunks: number,
    fileName: string,
    chunk: Blob
  ): Promise<{ chunkNumber: number; uploaded: boolean }> => {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('uploadId', uploadId);
    formData.append('chunkNumber', chunkNumber.toString());
    formData.append('totalChunks', totalChunks.toString());
    formData.append('fileName', fileName);

    const response = await apiRequest('/video-upload/multipart/upload', {
      method: 'POST',
      body: formData,
    });
    return response.data;
  },

  // 完成分片上传
  completeMultipartUpload: async (
    uploadId: string,
    fileName: string,
    totalChunks: number
  ): Promise<VideoUploadResult> => {
    const response = await apiRequest('/video-upload/multipart/complete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uploadId, fileName, totalChunks }),
    });
    return response.data;
  },

  // 取消分片上传
  abortMultipartUpload: async (uploadId: string): Promise<void> => {
    await apiRequest('/video-upload/multipart/abort', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uploadId }),
    });
  },

  // 获取视频访问URL
  getVideoUrl: async (key: string): Promise<{ url: string }> => {
    const response = await apiRequest(`/video-upload/url/${encodeURIComponent(key)}`);
    return response.data;
  },
};
